import {
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { FC, memo } from 'react';
import { BillingPerformanceKpiResponse } from 'src/hooks/useQueryBillingPerformances';

type Props = {
  kpis: BillingPerformanceKpiResponse | undefined;
  isLoading?: boolean;
};

type KPIItemProps = {
  label: string;
  value: string;
  bold?: boolean;
  color?: string;
  small?: boolean;
};

const KPIItem: FC<KPIItemProps> = ({ label, value, bold, color, small }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: small ? 0.75 : 1 }}>
    <Typography component="span" variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography
      component="span"
      variant={small ? 'body2' : 'body1'}
      fontWeight={bold ? 'bold' : 'medium'}
      sx={{ color: color ?? 'text.primary', whiteSpace: 'nowrap' }}
    >
      {value}
    </Typography>
  </Box>
);

const formatYen = (value: number): string => {
  if (Math.abs(value) >= 10000) {
    return `¥${(value / 10000).toFixed(1)}万`;
  }
  return `¥${value.toLocaleString()}`;
};

const KPICompactBar: FC<Props> = memo(({ kpis, isLoading = false }) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, mb: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const grossProfit = kpis?.gross_profit?.value ?? 0;
  const billing = kpis?.billing_amount?.value ?? 0;
  const payment = kpis?.payment_amount?.value ?? 0;
  const grossProfitRate = kpis?.gross_profit_rate?.value ?? 0;
  const totalCases = kpis?.total_cases?.value ?? 0;
  const totalWeight = kpis?.total_weight?.value ?? 0;
  const totalVolume = kpis?.total_volume?.value ?? 0;
  const totalDistance = kpis?.total_distance?.value ?? 0;

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      px: 3,
      py: 1.5,
      mb: 2,
      bgcolor: 'background.paper',
      borderBottom: 1,
      borderColor: 'divider',
      minHeight: 48,
      overflow: 'auto',
    }}>
      {/* 左側: 財務指標 */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 4, flexShrink: 0, whiteSpace: 'nowrap' }}>
        <KPIItem
          label="粗利"
          value={formatYen(grossProfit)}
          bold
          color={grossProfit >= 0 ? 'primary.main' : 'error.main'}
        />
        <KPIItem label="請求" value={formatYen(billing)} />
        <KPIItem label="支払" value={formatYen(payment)} />
        <KPIItem label="粗利率" value={`${grossProfitRate.toFixed(1)}%`} />
      </Box>
      {/* 右側: 業務指標 */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, flexShrink: 0, whiteSpace: 'nowrap', ml: 4 }}>
        <KPIItem label="案件" value={`${totalCases.toLocaleString()}件`} small />
        <KPIItem label="重量" value={`${(totalWeight / 1000).toFixed(1)}t`} small />
        <KPIItem label="体積" value={`${totalVolume.toLocaleString()}m³`} small />
        <KPIItem label="距離" value={`${totalDistance.toLocaleString()}km`} small />
      </Box>
    </Box>
  );
});

export default KPICompactBar;
